import { openChatPanel } from '@upright/ui/chat/chat-panel-state';
import AiSparklesIcon from '@upright/ui/icons/ai-sparkles-streamline-core';
import ArrowRightIcon from '@upright/ui/icons/arrow-right';
import { singleSideChip } from './chat-chips';
import { closeNetImpactDrillIn, drillInSingleImpactCode, NetImpactDrillInPayload } from '../state';
import styles from './ask-why-button.module.css';

interface Props {
  payload: NetImpactDrillInPayload;
  /** The tab's largest contributor, by product code and display name. */
  productCode: string;
  productName: string;
  valence: 'positive' | 'negative';
  impactLabel: string;
  entityName: string;
}

/**
 * The "why" question under a Positive / Negative tab's product list (PLT-7891): asks why the tab's
 * largest contributor has this impact, the question investors were left with after reading the
 * list. The Chat tab offers the same question, but readers rarely open that tab.
 *
 * It hands off to the chat side panel exactly as the Chat tab's starter chips do: close the
 * drill-in first, because both panels use the same right-edge slot.
 *
 * The visual is the quiet AI entry point for a white surface (design/60-ai-surface.md, "Preserve
 * the emphasis role when moving between backgrounds"): the yellow is concentrated in the small
 * sparkle disc, the same mark as the light header's Ask AI, and the button itself stays white and
 * outlined so it does not compete with the panel's data.
 */
export function AskWhyButton({ payload, productCode, productName, valence, impactLabel, entityName }: Props) {
  const chip = singleSideChip(
    { company: entityName, impact: impactLabel.toLowerCase() },
    { code: productCode, name: productName },
    valence,
    drillInSingleImpactCode(payload.target),
  );
  return (
    <button
      type="button"
      className={styles.root}
      title={chip.prompt}
      onClick={() => {
        closeNetImpactDrillIn();
        openChatPanel({
          prompt: chip.prompt,
          entityId: payload.entity.id,
          entityType: payload.entity.type,
          // The same analytics bucket as the Chat tab's starter chips.
          source: 'dimension_bar',
          pageContext: payload.pageContext,
          ...(chip.extra ? { extra: chip.extra } : {}),
        });
      }}
    >
      <span className={styles.mark}>
        <AiSparklesIcon width={14} height={14} aria-hidden="true" />
      </span>
      <span className={styles.label}>{chip.text}</span>
      <span className={styles.arrow} aria-hidden="true">
        <ArrowRightIcon width={14} height={14} />
      </span>
    </button>
  );
}
